import React, { useMemo, useState } from 'react';
import * as UI from '../components';
import ExportButton from '../ExportButton';

// defensive pulls
const common = UI.commonStyles || {
  spacing: { md: '1rem', lg: '1.5rem' },
};
const Card =
  UI.Card ||
  (({ title, icon, children }) => (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 6, padding: 16, marginBottom: 16 }}>
      {(title || icon) && (
        <div style={{ fontWeight: 600, marginBottom: 12 }}>
          {icon} {title}
        </div>
      )}
      {children}
    </div>
  ));
const DataTable =
  UI.DataTable ||
  (({ columns, data }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} style={{ textAlign: c.align || 'left', borderBottom: '1px solid #ddd', padding: '4px 8px' }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(data || []).map((row, i) => (
          <tr key={row.user_code || row.id || i}>
            {columns.map((c) => (
              <td key={c.key} style={{ textAlign: c.align || 'left', padding: '4px 8px', borderBottom: '1px solid #f4f4f4' }}>
                {c.render ? c.render(row[c.key], row) : row[c.key] ?? '-'}
              </td>
            ))}
          </tr>
        ))}
        {(!data || data.length === 0) && (
          <tr>
            <td colSpan={columns.length} style={{ textAlign: 'center', padding: '6px 0' }}>
              No data
            </td>
          </tr>
        )}
      </tbody>
    </table>
  ));
const SectionHeader =
  UI.SectionHeader ||
  (({ icon, title, subtitle }) => (
    <div style={{ marginBottom: 16, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
      <div style={{ fontWeight: 600 }}>
        {icon} {title}
      </div>
      {subtitle && <div style={{ fontSize: 12, color: '#666' }}>{subtitle}</div>}
    </div>
  ));
const LoadingState =
  UI.LoadingState ||
  (({ message }) => <div style={{ padding: 16 }}>{message || 'Loading...'}</div>);
const EmptyState =
  UI.EmptyState ||
  (({ title, description }) => (
    <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12 }}>{description}</div>
    </div>
  ));

// canonical comparator: use API-provided rank; fallback to current_tickets desc
const byRankThenCurrent = (a, b) => {
  const ra = Number(a?.rank);
  const rb = Number(b?.rank);
  const hasRa = Number.isFinite(ra);
  const hasRb = Number.isFinite(rb);

  if (hasRa && hasRb && ra !== rb) return ra - rb; // smaller rank first
  if (hasRa && !hasRb) return -1;
  if (!hasRa && hasRb) return 1;

  // fallback / tie-breaker: current_tickets desc
  const ca = Number(a?.current_tickets) || 0;
  const cb = Number(b?.current_tickets) || 0;
  if (cb !== ca) return cb - ca;

  // final tie-breaker: user_code asc to make it stable
  return String(a?.user_code || '').localeCompare(String(b?.user_code || ''));
};

const LeaderboardTab = ({
  loading,
  monthCur,
  monthPrev,
  curTop20 = [],
  prevTop20 = [],
  curAll,
  prevAll,
}) => {
  // same behavior: start with Top 20, allow toggling to All if All is supplied
  const [showAllCur, setShowAllCur] = useState(false);
  const [showAllPrev, setShowAllPrev] = useState(false);

  const curHasAll = Array.isArray(curAll) && curAll.length > curTop20.length;
  const prevHasAll = Array.isArray(prevAll) && prevAll.length > prevTop20.length;

  const curVisibleRaw = useMemo(() => {
    if (showAllCur && curHasAll) return curAll;
    return curTop20 || [];
  }, [showAllCur, curHasAll, curAll, curTop20]);

  const prevVisibleRaw = useMemo(() => {
    if (showAllPrev && prevHasAll) return prevAll;
    return prevTop20 || [];
  }, [showAllPrev, prevHasAll, prevAll, prevTop20]);

  // ✅ sort both sections by API "rank" (ascending); fallback to current_tickets desc
  const curVisible = useMemo(() => [...(curVisibleRaw || [])].sort(byRankThenCurrent), [curVisibleRaw]);
  const prevVisible = useMemo(() => [...(prevVisibleRaw || [])].sort(byRankThenCurrent), [prevVisibleRaw]);

  const columns = [
    {
      key: 'rank',
      label: 'Rank',
      width: '80px',
      render: (val) => {
        const n = Number(val);
        const medal = n === 1 ? '🥇' : n === 2 ? '🥈' : n === 3 ? '🥉' : '•';
        return `${medal} ${n || '-'}`;
      },
    },
    { key: 'user_code', label: 'User Code' },
    {
      key: 'current_tickets',
      label: 'Current Tickets',
      align: 'right',
      render: (v) => (v != null ? Number(v).toLocaleString() : '0'),
    },
    {
      key: 'previous_tickets',
      label: 'Previous Tickets',
      align: 'right',
      render: (v) => (v != null ? Number(v).toLocaleString() : '0'),
    },
  ];

  if (loading) {
    return <LoadingState message="Loading leaderboard..." />;
  }

  return (
    <div style={{ padding: common.spacing.lg }}>
      <SectionHeader
        icon="🏆"
        title="Monthly Leaderboard"
        subtitle={`${
          monthCur ? `Current: ${monthCur}` : 'Current month'
        } | ${monthPrev ? `Previous: ${monthPrev}` : 'Previous month'}`}
      />

      {/* Current Month */}
      <Card title={`📊 Current Month (${monthCur || '—'})`} icon="📈">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            {curHasAll && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowAllCur((x) => !x)}
              >
                {showAllCur ? 'Show Top 20' : 'Show More'}
              </button>
            )}
          </div>
          <ExportButton
            disabled={curVisible.length === 0}
            filenameBase={`leaderboard_${monthCur || 'current'}`}
            sheetName={`Leaderboard ${monthCur || 'Current'}`}
            columns={[
              { key: 'rank', header: 'Rank' },
              { key: 'user_code', header: 'User' },
              { key: 'current_tickets', header: 'Current Tickets' },
              { key: 'previous_tickets', header: 'Previous Tickets' },
            ]}
            rows={curVisible}
          />
        </div>

        {curVisible.length === 0 ? (
          <EmptyState title="No data" description="No leaderboard data available" />
        ) : (
          <DataTable columns={columns} data={curVisible} empty="No data available" />
        )}
      </Card>

      {/* Previous Month */}
      <Card title={`📊 Previous Month (${monthPrev || '—'})`} icon="📉">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            {prevHasAll && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowAllPrev((x) => !x)}
              >
                {showAllPrev ? 'Show Top 20' : 'Show More'}
              </button>
            )}
          </div>
          <ExportButton
            disabled={prevVisible.length === 0}
            filenameBase={`leaderboard_${monthPrev || 'previous'}`}
            sheetName={`Leaderboard ${monthPrev || 'Previous'}`}
            columns={[
              { key: 'rank', header: 'Rank' },
              { key: 'user_code', header: 'User' },
              { key: 'current_tickets', header: 'Current Tickets' },
              { key: 'previous_tickets', header: 'Previous Tickets' },
            ]}
            rows={prevVisible}
          />
        </div>

        {prevVisible.length === 0 ? (
          <EmptyState title="No data" description="No leaderboard data available" />
        ) : (
          <DataTable columns={columns} data={prevVisible} empty="No data available" />
        )}
      </Card>
    </div>
  );
};

export default LeaderboardTab;
